import React, {
  Suspense,
  useEffect,
  useState,
  useMemo,
  lazy,
  useContext,
} from "react";
import { Badge, Col, FloatButton, Image, Radio, Row, Space, Spin } from "antd";
import useQuery from "../../../../hooks/useQuery";
import { careItemsList } from "../../Options/careItemsOptions";
import { getOrderSheetApi } from "../../../../services/orderSheet.services";
import LoadingIndicator from "../../../Loaders/LoadingIndicator";
import { UserContext } from "../../../../context/UserContext";
import FloatingRecorder from "../../../PatientsIP/AiAssistant/FloatingRecorder";
import RecordModal from "../../../PatientsIP/AiAssistant/RecordModal";
import aiIcon from "../../../../assets/dashboardIcons/aiIcon.svg";
import "../../ipforms.css";
import "./OrderSheetMobile.css";
import "./OrderSheetTablet.css";
import { getCookie } from "../../../../helpers/localStorage";
import { Role } from "../../../../constants/defaultKeys";

// Dynamic import map
const componentMap = {
  nebulization: lazy(() => import("./Nebulisation")),
  iv_fluids: lazy(() => import("./IvFluids")),
  blood_products: lazy(() => import("./BloodProducts")),
  parental_nutrition: lazy(() => import("./ParenteralNutrition")),
  respiratory_care: lazy(() => import("./RespiratoryCare/RespiratoryCare")),
  urinary_catheter_care: lazy(() => import("./UrinaryCatheter")),
  open_wound_care: lazy(() => import("./WoundCare")),
  preventive_care: lazy(() => import("./PreventiveCare/PreventiveCare")),
  eye_care: lazy(() => import("./EyeCare/EyeCare")),
  feeding_tube: lazy(() => import("./FeedingTube")),
  pressure_sore: lazy(() => import("./PressureSore")),
  ostomy_care: lazy(() => import("./Ostomy")),
  specialist_consultation: lazy(() => import("./SpecialConsultation")),
};

function OrderSheet({ isSpecialArea = false }) {
  const query = useQuery();
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({});
  const [activeRadio, setActiveRadio] = useState(query?.care || "nebulization");
  const [openAiModal, setOpenAiModal] = useState(false);
  const [showFloatingRecorder, setShowFloatingRecorder] = useState(false);
  const [aiResponse, setAiResponse] = useState(null);
  const { userDetails } = useContext(UserContext);
  const [refresh, setRefresh] = useState(null);
  const refreshPage = () => setRefresh((prev) => !prev);
  const userRole = userDetails?.currRole || getCookie(Role);

  async function getData() {
    try {
      const response = await getOrderSheetApi(query?.patient);

      setFormData(response?.data?.data);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }
  const handleProcessComplete = (responseData) => {
    setAiResponse(responseData);
    setShowFloatingRecorder(false);
    setOpenAiModal(true);
  };

  useEffect(() => {
    if (query?.patient) getData();
  }, [query?.patient, refresh]);

  const ActiveComponent = useMemo(
    () => componentMap[activeRadio] || componentMap["nebulization"],
    [activeRadio]
  );

  function imageLoader(name) {
    try {
      return name === activeRadio
        ? require(`../../../../assets/careItemIcons/${name}_light.png`)
        : require(`../../../../assets/careItemIcons/${name}.png`);
    } catch (error) {
      console.error(`Image not found: ${name}`, error);
      return null;
    }
  }

  const getFormDataValue = () => {
    if (formData[activeRadio]) return formData[activeRadio];

    if (
      activeRadio === "preventive_care" &&
      Object.keys(formData).includes("position_change_schedule")
    )
      return formData;

    if (Object.keys(formData).some((item) => item.startsWith(activeRadio)))
      return formData;

    return undefined;
  };

  return (
    <>
      {loading ? (
        <LoadingIndicator message={"Loading..."} />
      ) : (
        <Row className="order-sheet-shell">
          <Col span={24} className="order-sheet-tabs-col">
            <Radio.Group
              className="order-sheet-tab-group"
              onChange={(e) => setActiveRadio(e.target.value)}
              buttonStyle="solid"
              value={activeRadio}
            >
              {careItemsList.map(({ label, key }) => {
                const showActiveDot = Object?.keys(formData).some(
                  (item) =>
                    item?.startsWith(key) && !formData[item]?.isallstopstate
                );
                return (
                  <Radio.Button
                    className="order-sheet-tab-button"
                    key={key}
                    value={key}
                  >
                    <span className="order-sheet-tab-inner">
                      <span className="order-sheet-tab-text">{label}</span>
                      <Image
                        preview={false}
                        src={imageLoader(key)}
                        className="order-sheet-tab-icon"
                        width={24}
                        height={24}
                      />
                    </span>
                    {showActiveDot && (
                      <Badge
                        color="green"
                        status="processing"
                        className="custom-badge-size order-sheet-tab-badge"
                      />
                    )}
                  </Radio.Button>
                );
              })}
            </Radio.Group>
          </Col>
          <Col span={24} className="order-sheet-form-col">
            <Suspense fallback={<Spin />}>
              <ActiveComponent
                formData={getFormDataValue()}
                getData={getData}
                isSpecialArea={isSpecialArea}
              />
            </Suspense>
          </Col>
        </Row>
      )}
      {userRole === "Doctor" && (
        <FloatButton
          shape="circle"
          type="primary"
          className="ai-assistant-float"
          icon={<img src={aiIcon} alt="AI Icon" width={25} height={32} />}
          onClick={() => {
            setShowFloatingRecorder(true);
            // setShowTooltip(false);
          }}
        />
      )}
      {showFloatingRecorder && (
        <FloatingRecorder
          onProcessComplete={handleProcessComplete}
          onClose={() => {
            setShowFloatingRecorder(false);
          }}
          userDetails={userDetails}
        />
      )}

      {/* Modal with AI Response */}
      {openAiModal && aiResponse && (
        <RecordModal
          open={openAiModal}
          setOpen={setOpenAiModal}
          refreshPage={refreshPage}
          aiResponse={aiResponse}
          startInFormStep={true}
        />
      )}
    </>
  );
}

export default OrderSheet;
